import { useEffect, useState } from 'react';

import { HistoryContent } from '../types';

interface UsePublishHistoryNavigationParams<T extends HistoryContent> {
  history: T[];
  liveVersion: T | undefined;
  publishUtc: number;
  originalVersionWhenInHistoryEditMode?: T | null;
  currentlyEditedVersion?: T;
}

export const usePublishHistoryNavigation = <T extends HistoryContent>({
  history,
  liveVersion,
  publishUtc,
  originalVersionWhenInHistoryEditMode,
  currentlyEditedVersion
}: UsePublishHistoryNavigationParams<T>) => {
  const [currentItemPublishUtc, setCurrentItemPublishUtc] = useState<number | null>(null);
  const [userPaginated, setUserPaginated] = useState(false);

  // Initialize currentItemPublishUtc to the live version on first load.
  useEffect(() => {
    if (currentItemPublishUtc == null && liveVersion?.metadata.publishUtc) {
      setCurrentItemPublishUtc(liveVersion.metadata.publishUtc);
    }
  }, [liveVersion, currentItemPublishUtc]);

  // Unless we are looking at a specific date, automatically switch to a specific page in the following order:
  // 1. the page that is currently being edited,
  // 2. the page that matches the `publishUtc` timestamp in the url params,
  // 3. the page that is currently live in history
  useEffect(() => {
    if (!userPaginated) {
      if (originalVersionWhenInHistoryEditMode && currentlyEditedVersion) {
        setCurrentItemPublishUtc(currentlyEditedVersion.metadata.publishUtc ?? null);
      } else if (history.length > 0 && publishUtc > 0) {
        const selectedItem = history.find((item) => item.metadata.publishUtc === publishUtc);
        if (selectedItem) {
          setCurrentItemPublishUtc(selectedItem.metadata.publishUtc ?? null);
        }
      } else {
        setCurrentItemPublishUtc(liveVersion?.metadata.publishUtc ?? null);
      }
    }
  }, [currentlyEditedVersion, history, originalVersionWhenInHistoryEditMode, publishUtc, userPaginated, liveVersion]);

  const liveIndex = history.findIndex((c) => c.metadata.publishUtc === liveVersion?.metadata.publishUtc);
  const fallbackIndex = liveIndex !== -1 ? liveIndex : history.length - 1;
  const matchedIndex =
    currentItemPublishUtc != null ? history.findIndex((c) => c.metadata.publishUtc === currentItemPublishUtc) : -1;
  const currentIndex = matchedIndex !== -1 ? matchedIndex : fallbackIndex;

  return {
    currentItemPublishUtc,
    setCurrentItemPublishUtc,
    userPaginated,
    setUserPaginated,
    currentIndex
  };
};
